import React, { useState } from 'react';
import produce from 'immer';
import {
  setBreakdownGroupBinning,
  setSelectedBinning,
} from '../../../../../../redux/slices/oldBuilder';
import { useAppDispatch, useAppSelector } from '../../../../../../redux/hooks';
import * as BucketsUI from '../BucketsUI';
import EnumValueSelector from './EnumValueSelector';
import { IEnumBinning } from '../../../../../../redux/slices/oldBuilder/binning/types';
import { BucketRowControlGroups } from '../../components';
import { MenuActions } from '../../types';
import { ElementOf } from 'ts-essentials';
import { builderSelectedOptionsSelector } from '../../../../../../redux/selectors/builder';
import { assertCondition } from '../../../../../../utils/types';
import { QueryType } from '../../../../../../utils/apollo/graphqlSchema';
import { isNil } from 'lodash';
import { defaultEnumBucket } from '../../../../../../redux/slices/oldBuilder/binning/defaults';

interface IProps {
  binning: IEnumBinning;
  groupIndex?: number;
}

type IEnumFieldValue = ElementOf<
  ElementOf<IEnumBinning['customBuckets']>['values']
>;

export default ({ binning, groupIndex }: IProps) => {
  const dispatch = useAppDispatch();

  const [renameEditorIndex, setRenameEditorIndex] = useState(-1);

  const selectedOptions = useAppSelector((state) =>
    builderSelectedOptionsSelector(state),
  );

  assertCondition(
    selectedOptions?.type === QueryType.Expression ||
      selectedOptions?.type === QueryType.Funnel ||
      selectedOptions?.type === QueryType.Retention ||
      selectedOptions?.type === QueryType.Path,
  );

  const selectedSubjectKey = selectedOptions.subject || undefined;

  const breakdownGroups = useAppSelector(
    ({ builder }) => builder.breakdown.groups,
  );

  const enumBuckets = binning.customBuckets;

  const onEnumBucketChange = (values: IEnumFieldValue[], index: number) => {
    const newBinning = produce(binning, (newBinning) => {
      newBinning.customBuckets[index].values = values;
      delete newBinning.namedBucketDef;
    });

    if (isNil(groupIndex)) {
      dispatch(
        setSelectedBinning({
          newBinning,
        }),
      );
    } else {
      dispatch(
        setBreakdownGroupBinning({
          groupIndex,
          newBinning,
        }),
      );
    }
    return;
  };

  /**
   * actions handler for clicking on Bucket Row Contextual Menu
   * @param action Add, Remove, or Edit name
   * @param index Group Index
   */
  const bucketRowActionsHandler = (action: MenuActions, index: number) => {
    if (action === MenuActions.Edit) {
      setRenameEditorIndex(index);
      return;
    }

    const newBinning = produce(binning, (newBinning) => {
      if (action === MenuActions.Add) {
        // insert new empty bucket on the next index.
        newBinning.customBuckets.splice(index + 1, 0, defaultEnumBucket);
      } else if (action === MenuActions.Remove) {
        // remove selected bucket
        newBinning.customBuckets.splice(index, 1);
      }

      delete newBinning.namedBucketDef;
    });

    if (isNil(groupIndex)) {
      dispatch(
        setSelectedBinning({
          newBinning,
        }),
      );
    } else {
      dispatch(
        setBreakdownGroupBinning({
          groupIndex,
          newBinning,
        }),
      );
    }
  };

  const onNameChangeHandler = (name: string, index: number) => {
    const newBinning = produce(binning, (newBinning) => {
      newBinning.customBuckets[index].name = name;
      newBinning.customBuckets[index].isCustomName = name.length > 0;

      delete newBinning.namedBucketDef;
    });

    if (isNil(groupIndex)) {
      dispatch(
        setSelectedBinning({
          newBinning,
        }),
      );
    } else {
      dispatch(
        setBreakdownGroupBinning({
          groupIndex,
          newBinning,
        }),
      );
    }
  };

  return (
    <BucketsUI.BucketsContainer>
      {enumBuckets.map((enumBucket, index) => (
        <BucketsUI.BracketContainer key={'enum-bucket-' + index}>
          <BucketsUI.Bracket>
            <BucketsUI.Text>{`${index + 1}.`}</BucketsUI.Text>
            <EnumValueSelector
              fieldKey={
                groupIndex !== undefined
                  ? breakdownGroups?.[groupIndex]?.field?.key
                  : selectedSubjectKey
              }
              setSelectedFieldValues={(val) => onEnumBucketChange(val, index)}
              selectedFieldValues={enumBucket.values}
            />
            <BucketRowControlGroups
              bucketRowActionsHandler={bucketRowActionsHandler}
              isOpenEditWindow={renameEditorIndex === index}
              rowIndex={index}
              bucketsLength={enumBuckets.length}
              onNameChangeHandler={onNameChangeHandler}
              customName={
                enumBucket.isCustomName && enumBucket.name
                  ? enumBucket.name
                  : undefined
              }
            />
          </BucketsUI.Bracket>
          {enumBucket.isCustomName && enumBucket.name && (
            <BucketsUI.CustomName>{enumBucket.name}</BucketsUI.CustomName>
          )}
        </BucketsUI.BracketContainer>
      ))}
    </BucketsUI.BucketsContainer>
  );
};
